/**
 * ProTableListForm — 独立受控「一条数据对多数组 + 多级表头」可编辑表格组件。
 *
 * 核心引擎：以 cooperation-row-height-showcase 为蓝本泛型化。
 * 渲染结构（多级 children 表头 / 每子列独立 cell 子项顶对齐 / 动态行高 / 批量 Popover /
 * 校验红框（scoped CSS 覆盖 border-color，**非** boxShadow）/ tooltip / R1R2R3 性能）与 cooperation **完全一致**，
 * 只是把业务模型抽成 `subArrays` / `columns` / `rules` 注入。零共享、不依赖 TableListForm。
 *
 * 用法要点：
 *  - getRowHeight 必须写在 virtual 对象里（顶层 props 不生效）；
 *  - SubCellContent / CellField 必须是 memo + 稳定回调（rowId/格值），否则整行全量重建。
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from '@bedrock/components';
import { useMemoizedFn } from 'ahooks';
import { CELL_CONTROL_WIDTH_STYLE, CELL_SELECT_DROPDOWN_PROPS } from './cellControlStyle';
import {
  SUB_ROW,
  pxRatio,
  getRowHeightPx,
  computeRowHeight,
  computeRowSignature,
} from './rowMetrics';
import { ERR_STYLE, ErrorWrap, useDebouncedErrorVisible } from './cellError';
import { buildBedrockColumns, OP_W, DRAG_W } from './buildBedrockColumns';
import { useControlledMutations } from './useControlledMutations';
import { useErrorLocator } from './useErrorLocator';
import { ProTablePlusForm } from '@mpc/common/src/components/ProTablePlusForm';
import type { ProTablePlusFormRef } from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import type {
  ChildBase,
  RowBase,
  ProTableListFormHandle,
  ProTableListFormProps,
} from './types';

// ============================================================
// 常量（避免魔法数字）
// ============================================================
/**
 * `scrollToLastRow` 的最大重滚帧数。
 *
 * 新增行后布局要到下一帧才稳定（见该函数注释），故需分帧重滚；此值为兜底上限，
 * 防止「内容持续增高」等异常情况下无限滚下去。约 6 帧 ≈ 100ms，远小于可感知延迟。
 */
const SCROLL_TO_LAST_ROW_MAX_FRAMES = 6;
// 列宽占位（OP_W / DRAG_W）与单元格对齐（topAlignCell / subCellAlign）已移至 ./buildBedrockColumns
// —— OP_W / DRAG_W 仍被下面的 tableScrollX 使用，故 import 回来。

// 行高与「组是否可见/是否占添加入口」的判据已收敛到 ./rowMetrics（唯一判据源）：
// SUB_ROW / pxRatio / rowHeightCache / sideHeight / subHasAddRow / subRowVisible
// 及 computeRowHeight / computeRowSignature / rowHeightPx / getRowHeightPx。

// 校验「首个错误」的查找与滚动定位（含 stickyLeftWidth / stickyRightWidth /
// scrollCellIntoViewX / ROW_SCROLL_OFFSET / findErrorAnchor）已移至 ./useErrorLocator。

// 红框样式契约（ERR_STYLE / ERR_CELL_CLASS / ADD_ERR_CLASS）与格内错误提示链
//（INPUT_VALIDATE_DEBOUNCE_MS / useDebouncedErrorVisible / ErrorWrap）已移至 ./cellError。

// 校验规则引擎（REQUIRED_MESSAGE / subColRules / subColEditable / isEmpty / matchRuleType /
// sizeOfValue / runOneRule / validateValue）已移至 ./fieldRules。

// 单元格控件集合（CellField / SubCellContent / renderSubColCell / renderOpColCell /
// subItemRowStyle / subAddRowStyle）已移至 ./renderCells。

// 拖拽子项格（DRAG_GHOST_CLASS / renderDragColCell）已移至 ./dragCell。

// 列头挂件已移至 ./columnHeaderWidgets
//（ColumnFixedButtonLayer / BatchEditCellTitle / groupTitle / subTitle）

// ============================================================
// 主组件
// ============================================================
// Props / Handle 的唯一定义源在 ./types（本文件不再重复声明，避免两份漂移）。
export type { ProTableListFormProps, ProTableListFormHandle };

function ProTableListFormInner<TRow extends RowBase, TChild extends ChildBase>(
  props: ProTableListFormProps<TRow, TChild>,
  ref: React.ForwardedRef<ProTableListFormHandle>,
) {
  const { value, onChange, subArrays, columns, rules = {}, height, maxHeight, minHeight } = props;
  const tableRef = useRef<ProTablePlusFormRef>(null);
  /** 表格外层容器：供校验定位时自主查找滚动容器与目标行（不依赖 @bedrock table.scrollTo） */
  const tableWrapRef = useRef<HTMLDivElement>(null);

  // 分场景 overscan（R3：编辑小窗口促输入 / 滚动大窗口防白屏）
  const OVERSCAN_EDIT = 3;
  const OVERSCAN_SCROLL = 6;
  const [overscanMode, setOverscanMode] = useState<'edit' | 'scroll'>('scroll');
  /**
   * 全量校验态：外部触发校验（提交 / 「校验」按钮）后置 true，使全表必填格标红。
   * 输入期则只对「用户实际编辑过的格」生效（见 CellField / SubCellContent 的 touched），避免扩散。
   */
  const [forceValidate, setForceValidate] = useState(false);
  /**
   * 表单实例：**仅供 `FieldRuleRender`（动态规则）求值**，引擎自身的校验不依赖它
   * （校验走 `validateValue` 的同步实现，见 runOneRule）。
   * 传入 `FieldRuleRender` 的 `form` 参数即此实例（类型收敛为 unknown，业务按需断言）。
   */
  const [ruleForm] = Form.useForm();
  const editTimerRef = useRef(0);
  const markEditing = useMemoizedFn(() => { setOverscanMode('edit'); window.clearTimeout(editTimerRef.current); editTimerRef.current = window.setTimeout(() => setOverscanMode('scroll'), 1200); });
  const markScrolling = useMemoizedFn(() => { setOverscanMode('scroll'); window.clearTimeout(editTimerRef.current); });
  useEffect(() => {
    const onScroll = () => markScrolling();
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [markScrolling]);

  // ============ handlers（通用，稳定回调） ============
  // handleChange 是唯一的「原样透传」入口（不参与写回语义），有意留在容器。
  const handleChange = useMemoizedFn((next: TRow[]) => onChange(next));
  // 写回 handler 集合（9 个）已外移到 ./useControlledMutations
  // —— 内部**逐个** useMemoizedFn，identity 永久稳定，故可直接进 bedrockColumns 的依赖数组。
  // ⚠️ 禁止把这里的解构改成 useCallback/裸箭头包装：会让 identity 每帧变，
  //    经 columns → bedrockColumns → @bedrock 三级 memo 全线失效（不报错、只变卡+失焦）。
  const {
    updateRow,
    patchItem,
    requestAddItem,
    reorderItems,
    removeItem,
    handleCopyRow,
    handleDeleteRow,
    handleBatchApply,
  } = useControlledMutations({ value, onChange, subArrays, markEditing });

  // ============ 行高（按子数组数量 + 有无添加行 + 行级可见性） ============
  const rowHeight = useMemoizedFn((row: TRow): number => computeRowHeight(row, subArrays));
  const rowSignature = useMemoizedFn((row: TRow): string => computeRowSignature(row, subArrays));

  // 内容总高：Σ 每行动态行高（含子项），与 getRowHeight 同一 px 基准（×pxRatio + 2）
  // 供「容器高度随内容/子项自适应」的 itemHeight 估算（VirtualBody 的 getScrollHeight = min(scroll.y, data.length × itemHeight)）
  const contentHeight = useMemo(
    () => value.reduce((sum, row) => sum + (rowHeight(row) * pxRatio() + 2), 0),
    [value, subArrays, rowHeight],
  );

  // 容器/视窗高：传 maxHeight 时按内容自适应封顶（scroll.y = min(maxHeight, contentHeight)），
  // 未传则回退固定 height（保持原行为）。
  const scrollHeight = maxHeight ? Math.max(minHeight ?? 0, Math.min(maxHeight, contentHeight)) : (height ?? 520);
  // 横向滚动宽度 = 列宽总和（含 subArray 子列 + 子项操作列 + 拖拽列），供 scroll.x 触发表格横向滚动、
  // 使操作列（fixed:'right'）始终固定于右侧（用户要求：避免列多时布局溢出/操作列滚走）。
  const tableScrollX = useMemo(() => {
    let w = 0;
    for (const c of columns) {
      if (c.kind === 'doc') w += c.doc.width || 0;
      else if (c.kind === 'custom') w += c.custom.width || 0;
      else if (c.kind === 'subArray') {
        w += c.sub.columns.reduce((s, col) => s + (col.width || 0), 0) + OP_W;
        if (c.sub.sortable) w += DRAG_W;
      }
    }
    return w;
  }, [columns]);
  // 有固定列（fixed:left/right，如「钉在左侧」）时关闭虚拟滚动——@bedrock table 虚拟滚动下 fixed-left 的 body 单元
  // 不固定（FixedHolder 只覆盖 header），非虚拟 Body 走完整 fixed 渲染。
  //
  // ⚠️ 2026-09-15 实测记录：曾尝试把判据收窄为「只判 left」（意图让「无钉住列」时恢复虚拟滚动以缓解
  // 大表拖拽列宽时的卡顿），结果**表格直接渲染为空**（thead 无标题、tbody 无内容、容器高度 0），
  // 已回滚。说明虚拟滚动与固定列的组合在 @bedrock table 下限制比注释所述更严格，
  // 若要再动此处，必须先单独验证「虚拟滚动 + 固定列」的各种组合。
  const hasFixed = useMemo(() => columns.some((c) => (c.kind === 'custom' && !!c.custom.fixed) || (c.kind === 'doc' && !!c.doc.fixed)), [columns]);
  /**
   * 左侧固定列的「集合 + 顺序」签名，仅作为重建 key 的一部分，不参与渲染。
   *
   * 【为何需要】@bedrock ProTable 内部会按 fixed 把列重排成 left → center → right，
   * 且该顺序**会滞留**：取消钉住后该列只是回到 center 组，但在 bedrock 的内部顺序里
   * 它已排在最前 → 「钉住再取消钉住」后列回不到原位置（#46482）。
   *
   * 【为何不能沿用 hasFixed】下方 key 原以 `hasFixed` 切换，本意是靠强制重建清除这类残留，
   * 但 hasFixed 的判据是「**任意**列有 fixed」，而操作列恒为 fixed:'right'
   * → hasFixed 恒为 true、key 永不变化、重建**从不发生**（实测：DOM 节点跨钉住操作未重建）。
   *
   * 故改为「左侧固定列的 key 序列」：只有增删左侧固定列时才变，
   * 精确对应需要清除的那个残留（右侧固定列恒定，不计入）。
   */
  const fixedLeftSignature = useMemo(
    () =>
      columns
        .filter((c) => (c.kind === 'custom' && c.custom.fixed === 'left') || (c.kind === 'doc' && c.doc.fixed === 'left'))
        .map((c) => (c.kind === 'custom' ? c.custom.key : c.kind === 'doc' ? c.doc.key : ''))
        .join('|'),
    [columns],
  );

  const virtual = useMemo(() => ({
    maxRows: hasFixed ? Number.MAX_SAFE_INTEGER : 0,
    useAntd: false,
    rowContentMemo: true,
    overscanCount: overscanMode === 'edit' ? OVERSCAN_EDIT : OVERSCAN_SCROLL,
    // 关键：把 itemHeight 设为「平均渲染行高 = 内容总高/行数」（与 getRowHeight 同基准含 pxRatio+2），
    // 使 data.length × itemHeight ≈ 真实内容总高，容器随 KPI/权益子项增加而自适应展开，封顶由 scroll.y 控制。
    itemHeight: value.length ? contentHeight / value.length : SUB_ROW,
    getRowHeight: (row: TRow) => getRowHeightPx(row, subArrays),
    getRowHeightSignature: (row: TRow) => rowSignature(row),
  }), [overscanMode, subArrays, rowHeight, rowSignature, contentHeight, value, hasFixed]);

  // ============ 生成 @bedrock 列（多级 children / 每子列独立 cell） ============
  // 列翻译已外移到 ./buildBedrockColumns（纯函数）。
  // ⚠️ useMemo 与下面这行依赖数组**留在此处且逐字冻结**（门禁 scripts/check-deps-array.mjs）：
  //    漏 forceValidate → 首次点「校验」列不重建 → CellField 收不到新 forceValidate → 全表标红不出现
  //    （不报错、不崩溃）。另有**有意省略**项 ruleForm（Form.useForm() 实例 identity 稳定），勿「补全」。
  const bedrockColumns: AddGroupFormColumn[] = useMemo(() => buildBedrockColumns({
    columns, rules, forceValidate, ruleForm,
    handleBatchApply, updateRow, patchItem, requestAddItem, removeItem, reorderItems,
  }), [columns, subArrays, rules, handleBatchApply, updateRow, patchItem, requestAddItem, removeItem, reorderItems, forceValidate]);

  // ============ 校验定位（引擎：按列展示顺序遍历，遇错即停） ============
  // 首个错误的查找 + 滚动定位（含左右钉住列遮挡规避）已外移到 ./useErrorLocator。
  // 对外只暴露 validateOnly（只校验、不滚动）/ validateAndScroll（校验 + 定位）两个入口。
  const { validateOnly, validateAndScroll } = useErrorLocator({
    value, columns, subArrays, rules, ruleForm, tableWrapRef, setForceValidate,
  });

  /**
   * 滚动到最后一行（新增行后由业务调用）。
   *
   * 【为什么必须有】表格高度随内容自适应，但**受 `maxHeight` 封顶**（业务传 60% 屏高）。
   * 封顶后表格内部滚动，新追加的行落在视口外或只露出一截。
   * 引擎已知滚动容器（与 useErrorLocator 同一处实测结论：横纵滚动都挂在 `.rock-table-body`），
   * 故由引擎提供该能力，业务不必重复探测 DOM。
   *
   * 【为何滚到底而不是滚到「最后一行元素」】行高由内容（子项数）动态决定，
   * 滚到底即可保证最后一行完整可见，且不依赖行元素是否已被渲染。
   *
   * ⚠️ 【必须分帧重滚，不能只滚一次】只滚一次会停在「加行前」的底部：新增行写回后，
   * @bedrock 的 body 高度与行高都要等 React 提交 + 落一轮布局才到终值，第一帧读到的
   * `scrollHeight` 还是旧的，滚完仍差一截 —— 现象正是「新增行只露出半截、下半埋在滚动区外」。
   * 故每帧滚到底并比较收敛（滚到底是幂等的，多滚几次无副作用），
   * 直到 `scrollTop` 不再变化或达上限帧数为止。
   */
  const scrollToLastRow = useMemoizedFn(() => {
    const scroller = tableWrapRef.current?.querySelector('.rock-table-body') as HTMLElement | null;
    if (!scroller) return;
    let frames = 0;
    let lastTop = -1;
    const scrollToBottom = () => {
      scroller.scrollTop = scroller.scrollHeight;
      frames += 1;
      // 收敛判据：滚到底后 scrollTop 不再增长（内容高度已稳定）→ 停；
      // 上限帧数兜底：内容持续增高（如子项异步渲染）时也不会无限滚下去。
      if (frames < SCROLL_TO_LAST_ROW_MAX_FRAMES && scroller.scrollTop !== lastTop) {
        lastTop = scroller.scrollTop;
        requestAnimationFrame(scrollToBottom);
      }
    };
    requestAnimationFrame(scrollToBottom);
  });

  // ============ Handle ============
  React.useImperativeHandle(ref, () => ({
    locateToError: () => false,
    // 同步「校验 + 滚动定位」：外部触发（提交、「校验」按钮）用于快速定位（true = 有错并已定位）
    validateAndLocate: () => validateAndScroll(),
    // 异步「只校验、不滚动」：对齐 ProTablePlusForm.validateFields 语义，供输入过程等场景使用，
    // 避免把表格滚走；有错时 reject，调用方以 catch 判定失败
    validateFields: () =>
      new Promise<void>((resolve, reject) => {
        if (validateOnly()) reject(undefined);
        else resolve();
      }),
    addRow: () => {},
    removeRow: handleDeleteRow,
    copyRow: handleCopyRow,
    batchApply: handleBatchApply,
    // 新增行后把新行滚入可视区（表格高度被 maxHeight 封顶时新行会在视口外，见 scrollToLastRow）
    scrollToLastRow,
  }), [validateOnly, validateAndScroll, handleDeleteRow, handleCopyRow, handleBatchApply, scrollToLastRow, value]);

  return (
    <Form form={ruleForm} component={false}>
      <style>{ERR_STYLE}</style>
      {/* 外层容器：供校验定位自主查找滚动容器与目标行（见 scrollToRow） */}
      <div ref={tableWrapRef} className="w-full">
      <ProTablePlusForm
        /**
         * key 随「是否存在固定列」切换而重建。
         *
         * 原因：@bedrock table 用 virtual.maxRows 作为虚拟滚动开关阈值（Table.js:287-289），
         * 有/无固定列会让 enableVirtual 在 VirtualBody 与 Body 两套实现间来回切换
         * （Table.js:492），该切换会残留旧的固定列样式——表现为「取消钉住后该列仍停在
         * 被钉住的位置、顺序不恢复」。重建可确保列顺序与固定态按最新 columns 重新计算。
         * 仅在 0 ↔ 1 个固定列时重建；固定列之间增删不触发。
         */
        key={hasFixed ? `pro-table-with-fixed-${fixedLeftSignature}` : 'pro-table-plain'}
        ref={tableRef}
        columns={bedrockColumns}
        value={value}
        onChange={handleChange}
        rowKey="id"
        /**
         * viewBase=1：让列宽保持**数字**（走 tableColumnWidthViewBase）而不是被转成 rem 字符串。
         *
         * 【为何必须】列宽可拖拽（resizable）底层用 react-resizable，它要求 width 是数字；
         * 默认路径 tableColumnWidthToRem 会把数字变成 "165rem" 这样的字符串 → 把手能渲染但**拖不动**。
         * 同坑此前已在 OA/MonthlyConfirm/DetailTable.tsx 记录（FR-13）。
         * viewBase=1 时宽度值不变（×1，仍是原来那个数字），只是不再转 rem。
         */
        viewBase={1}
        virtual={virtual}
        scroll={{ x: tableScrollX, y: scrollHeight }}
        verticalBorder
      />
      </div>
    </Form>
  );
}

export const ProTableListForm = React.forwardRef(ProTableListFormInner) as <TRow extends RowBase, TChild extends ChildBase>(
  props: ProTableListFormProps<TRow, TChild> & { ref?: React.ForwardedRef<ProTableListFormHandle> },
) => React.ReactElement;

export default ProTableListForm;
